import {
	getSearchHistoryDashboard,
	postSearchHistoryDashboard,
	deleteSearchHistoryDashboard,
	getSearchSuggestions,
	getTrendingTags,
	getCourseSuggestion,
	getAnonymousSearchHistory,
	postAnonymousSearchHistory,
	deleteAnonymousSearchHistory,
} from "@/utils/apiFunctions";
import { config } from "@/config";
import { hasAccess, getGuestUUID, ensureGuestUUID } from "@/utils/tokenCheck";
import EventBus from "@/utils/EventBus";

export default {
	data() {
		return {
			isSearchLoading: false,
			searchSuggestion: [],
			searchSuggestionRequestId: 0,
			recentSearches: [],
			trendingTags: [],
			isRecentSearchLoading: false,
			isTrendingTagLoading: false,
			config,
			searchQuery: "",
		};
	},
	created() {
		if (!this.hasAccess()) {
			ensureGuestUUID();
		}

		EventBus.$on("login-success", this.handleLoginSuccessRecentSearches);
		EventBus.$on("onboarding-fetch-post-login", this.handleLoginSuccessRecentSearches);
		EventBus.$on("refresh-recent-searches", this.handleRefreshRecentSearches);
	},
	beforeDestroy() {
		EventBus.$off("login-success", this.handleLoginSuccessRecentSearches);
		EventBus.$off("onboarding-fetch-post-login", this.handleLoginSuccessRecentSearches);
		EventBus.$off("refresh-recent-searches", this.handleRefreshRecentSearches);
	},
	methods: {
		hasAccess() {
			return hasAccess();
		},
		async trackSearchInteraction(query, type = "job") {
			if (!query) return;
			try {
				let numericType = 1;
				if (type === "assessment" || type === "assessments" || type === 2)
					numericType = 2;
				else if (type === "courses" || type === "course" || type === 3)
					numericType = 3;

				const payload = { searchQuery: query, type: numericType };

				//Handle Guest vs Logged-in search history
				if (this.hasAccess()) {
					// User is logged in, save to primary search history
					await postSearchHistoryDashboard(payload);
				} else {
					// User is Guest, save to anonymous history using browser UUID
					payload.uuid = ensureGuestUUID();
					await postAnonymousSearchHistory(payload);
				}
				// Only emit event - don't call getRecentSearches directly to avoid duplicate GET requests
				EventBus.$emit("refresh-recent-searches", type);
			} catch (error) {
				console.error("Tracking Error:", error);
			}
		},
		handleRefreshRecentSearches(type) {
			const currentTab = this.selectedTab || "job";
			const eventType = type || "job";
			// Refresh if the updated history type matches current tab
			if (eventType === currentTab) {
				this.getRecentSearches(eventType);
			}
		},
		handleLoginSuccessRecentSearches() {
			const currentTab = this.selectedTab || "job";
			this.getRecentSearches(currentTab);
		},
		async getRecentSearches(type = "job", count = 10) {
			if (this.isRecentSearchLoading) return; // Prevent duplicate calls if one is already in progress

			this.isRecentSearchLoading = true;
			try {
				let numericType = 1;
				if (type === "assessment" || type === "assessments" || type === 2)
					numericType = 2;
				else if (type === "courses" || type === "course" || type === 3)
					numericType = 3;

				const payload = { type: numericType, count };
				let res;

				//Fetch history from correct source based on login state
				if (this.hasAccess()) {
					// Fetch registered user history
					res = await getSearchHistoryDashboard(payload);
				} else {
					// Fetch Guest history using UUID from cookies
					const uuid = getGuestUUID();
					if (!uuid) {
						this.recentSearches = [];
						return;
					}
					payload.uuid = uuid;
					res = await getAnonymousSearchHistory(payload);
				}

				if (res.status == 200) {
					// Safety fallback: handles different backend response structures (nested vs direct)
					this.recentSearches = res.data.data.data || res.data.data || [];
				}
			} catch (error) {
				console.error("Recent Search Error:", error);
			} finally {
				this.isRecentSearchLoading = false;
			}
		},
		async clearRecentSearches(type = null) {
			const searches = this.recentSearches;
			if (!searches || searches.length === 0) return;
			try {
				const resolvedType =
					type && typeof type === "string" ? type : this.selectedTab || "job";
				let numericType = 1;
				if (
					resolvedType === "assessment" ||
					resolvedType === "assessments" ||
					resolvedType === 2
				)
					numericType = 2;
				else if (
					resolvedType === "courses" ||
					resolvedType === "course" ||
					resolvedType === 3
				)
					numericType = 3;

				const searchIDs = searches
					.map((item) => item.searchID)
					.filter((id) => id);

				if (this.hasAccess()) {
					const payload = { searchIDs, type: numericType, deleteAll: true };
					await deleteSearchHistoryDashboard(payload);
				} else {
					const uuid = getGuestUUID();
					if (uuid) {
						await deleteAnonymousSearchHistory({ uuid, searchIDs });
					}
				}
				this.recentSearches = [];
				EventBus.$emit("refresh-recent-searches", type);
			} catch (error) {
				console.error("Clear Error:", error);
				this.recentSearches = [];
			}
		},
		async getSearchSuggestionsData(query, type = "job") {
			const normalizedQuery = (query ?? "").toString().trim();
			if (!normalizedQuery) {
				this.searchSuggestion = [];
				this.searchSuggestionRequestId += 1;
				return;
			}

			// requestId prevents "Race Conditions" where slow requests overwrite newer ones
			const requestId = ++this.searchSuggestionRequestId;
			this.isSearchLoading = true;
			try {
				let res;
				let nextSuggestions = [];
				if (type === "courses" || type === "course" || type === 3) {
					// Use searchQuery as the primary parameter for course suggestions
					res = await getCourseSuggestion({ searchQuery: normalizedQuery });
					if (res.status == 200) {
						nextSuggestions =
							res.data?.data?.courseInfo?.data || res.data?.data?.data || [];
					}
				} else {
					let payload = {
						searchQuery: normalizedQuery,
						type:
							type === "assessment" || type === "assessments" || type === 2
								? 2
								: 1,
					};
					res = await getSearchSuggestions(payload);
					if (res.status == 200) {
						nextSuggestions = res.data?.data?.Detail || [];
					}
				}

				if (requestId === this.searchSuggestionRequestId) {
					this.searchSuggestion = nextSuggestions;
				}
			} catch (error) {
				console.error("Search API Error:", error);
				if (requestId === this.searchSuggestionRequestId) {
					this.searchSuggestion = [];
				}
			} finally {
				if (requestId === this.searchSuggestionRequestId) {
					this.isSearchLoading = false;
				}
			}
		},
		onSearchInput(event) {
			const query = (event?.target?.value ?? event ?? "").toString().trim();
			this.searchQuery = query;
			this.getSearchSuggestionsData(query, this.selectedTab);
		},
		async getTrendingTagsData(type = "job") {
			this.isTrendingTagLoading = true;
			try {
				let typeParam = type;
				if (type === 1 || type === "job" || type === "jobs") typeParam = "job";
				else if (type === 2 || type === "assessment" || type === "assessments")
					typeParam = "assessment";
				else if (type === 3 || type === "course")
					typeParam = "course";

				const res = await getTrendingTags({ type: typeParam });
				if (res.status == 200) {
					const trendingData = res.data?.data;
					this.trendingTags = Array.isArray(trendingData)
						? trendingData
						: Array.isArray(trendingData?.data)
						? trendingData.data
						: Array.isArray(trendingData?.items)
						? trendingData.items
						: [];
				}
			} catch (error) {
				console.error("Trending Tags Error:", error);
			} finally {
				this.isTrendingTagLoading = false;
			}
		},
		handleEnterSearch(query, type = "job") {
			const isEventObject =
				query &&
				typeof query === "object" &&
				(query.target || query.currentTarget || query.type || query.key);
			const eventStringPattern = /^\[object\s+.+Event\]$/;
			const normalizedRawQuery = isEventObject
				? query?.target?.value || this.searchQuery
				: query;
			let resolvedQuery = (normalizedRawQuery ?? "").toString().trim();

			if (eventStringPattern.test(resolvedQuery)) {
				resolvedQuery = (this.searchQuery || "").toString().trim();
			}
			const resolvedType = isEventObject ? this.selectedTab || type : type;

			if (!resolvedQuery) return;
			this.trackSearchInteraction(resolvedQuery, resolvedType);

			if (
				resolvedType === "job" ||
				resolvedType === "jobs" ||
				resolvedType === 1
			) {
				this.$router.push({
				path: "/job/search",
				query: {
					searchQuery: resolvedQuery,
					tab: "hire",
					fromPage: this.$route.name,
				},
				}).catch(() => {});
			} else if (
				resolvedType === "assessment" ||
				resolvedType === "assessments" ||
				resolvedType === 2
			) {
				this.$router.push({
					path: "/assessment/progress",
					query: { searchQuery: resolvedQuery },
				}).catch(() => {});
			} else if (
				resolvedType === "courses" ||
				resolvedType === "course" ||
				resolvedType === 3
			) {
				window.location.href = `${
					this.config.trainingBase
				}/filteredcoursesview?sortBy=publishedAt&search=${encodeURIComponent(
					resolvedQuery
				)}`;
			} else {
				this.$router.push({
					path: "/job/search",
					query: { searchQuery: resolvedQuery, tab: "hire" },
				}).catch(() => {});
			}
		},
	},
};
